import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

/**
 * A page-level registry of open permission editors, so one Save bar can save every
 * admin edited on the page.
 *
 * Deliberately not a lifted state layer: each CompanyRolesEditor keeps its own
 * draft and its own save(), and only publishes a small summary here. The store is a
 * plain closure created once, so the context value identity never changes and a
 * registration can't cascade re-renders through a 25-row list.
 */

export interface RolesEditorSummary {
  id: string;
  /** The admin's or company's name — only the host knows it. */
  label: string;
  isDirty: boolean;
  canEdit: boolean;
  saving: boolean;
  invalidReason: string | null;
  /** "Apply to all" would wipe per-resource exceptions, so the bar must confirm. */
  needsExceptionsConfirm: boolean;
}

export type RolesEditorSaveOutcome =
  | { status: 'saved' }
  | { status: 'skipped' }
  | { status: 'failed'; error: any };

export interface RolesEditorActions {
  save: (opts?: { silent?: boolean }) => Promise<RolesEditorSaveOutcome>;
  discard: () => void;
}

export interface RolesEditorEntry {
  summary: RolesEditorSummary;
  actions: React.MutableRefObject<RolesEditorActions>;
}

export interface RolesSaveBarStore {
  register: (summary: RolesEditorSummary, actions: React.MutableRefObject<RolesEditorActions>) => () => void;
  publish: (summary: RolesEditorSummary) => void;
  getEntries: () => RolesEditorEntry[];
  subscribe: (listener: () => void) => () => void;
}

// undefined means no page-level bar on this screen, and every editor keeps its own
// inline Save/Discard bar. That is the fallback for hosts that aren't wired up.
export const RolesSaveBarContext = createContext<RolesSaveBarStore | undefined>(undefined);

const summariesEqual = (a: RolesEditorSummary, b: RolesEditorSummary) =>
  a.label === b.label &&
  a.isDirty === b.isDirty &&
  a.canEdit === b.canEdit &&
  a.saving === b.saving &&
  a.invalidReason === b.invalidReason &&
  a.needsExceptionsConfirm === b.needsExceptionsConfirm;

const createStore = (): RolesSaveBarStore => {
  const entries = new Map<string, RolesEditorEntry>();
  const listeners = new Set<() => void>();
  // Rebuilt only when the map mutates, so subscribers can compare by identity.
  let snapshot: RolesEditorEntry[] = [];

  const emit = () => {
    snapshot = Array.from(entries.values());
    listeners.forEach(listener => listener());
  };

  return {
    register: (summary, actions) => {
      entries.set(summary.id, { summary, actions });
      emit();
      return () => {
        entries.delete(summary.id);
        emit();
      };
    },
    // Summaries change on every keystroke-ish interaction; a no-op publish must not
    // wake the bar, or an editor re-rendering for unrelated reasons re-renders it.
    publish: summary => {
      const existing = entries.get(summary.id);
      if (!existing || summariesEqual(existing.summary, summary)) return;
      entries.set(summary.id, { ...existing, summary });
      emit();
    },
    getEntries: () => snapshot,
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const RolesSaveBarProvider = ({ children }: { children: React.ReactNode }) => {
  const storeRef = useRef<RolesSaveBarStore>();
  if (!storeRef.current) storeRef.current = createStore();
  return <RolesSaveBarContext.Provider value={storeRef.current}>{children}</RolesSaveBarContext.Provider>;
};

/** Registration order, which is DOM order — so any report matches the screen. */
export const useRolesSaveBarEntries = (): RolesEditorEntry[] => {
  const store = useContext(RolesSaveBarContext);
  const [entries, setEntries] = useState<RolesEditorEntry[]>(() => store?.getEntries() || []);

  useEffect(() => {
    if (!store) return;
    setEntries(store.getEntries());
    return store.subscribe(() => setEntries(store.getEntries()));
  }, [store]);

  return entries;
};

/** One editor's summary, for a row that wants to flag its own unsaved changes. */
export const useRolesEditorSummary = (id?: string): RolesEditorSummary | undefined => {
  const entries = useRolesSaveBarEntries();
  return useMemo(() => (id ? entries.find(entry => entry.summary.id === id)?.summary : undefined), [
    entries,
    id,
  ]);
};
